import { FC } from 'react';
import { Styled } from './index.styles';
import moment from 'moment';
import { NAVIGATION_URL_CONSTANTS } from '../../organisms/HotelInformation/config';
import { useCheckBreakpoint } from '../../hooks';
import clsx from 'clsx';
import { useConditionalHQVContext } from '../../hooks/lib/useConditionalHQVContext';
import { hardCodedAllLocale } from '../../constants';
import { addSubDirectoryPrefix, getTimeInArabicTimeOfDayFormat } from '../../utils/src/helper';
import { useConditionalPageContextSERP, useConditionalServerDataHotelInformationSERP } from '../../hooks';

const toSentenceCaseLabel = (label?: string): string => {
  if (!label) return '';
  const normalized = label.toLowerCase();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

/* eslint-disable @typescript-eslint/no-explicit-any */
export const HotelInformation: FC<{ hotelInformationData: any }> = ({ hotelInformationData }) => {
  const { jsonContent } = useConditionalServerDataHotelInformationSERP();
  // Read HQV state via the flag-aware hook. When rendered inside the HQV modal, the parent
  // wraps children with HQVModalFlagContext ('serp' or 'adf') so this call picks up the correct
  // store (Zustand when the page's *_ZUSTAND flag is on, React Context otherwise).
  const { hqvState } = useConditionalHQVContext();
  const { pageVal, currentLocale } = useConditionalPageContextSERP();

  const hotelData = hotelInformationData?.property;
  const accessibility = jsonContent?.accessibilityLabel;

  // get dateFormat for different locales
  let checkInDate = '';
  let checkOutDate = '';
  const localeCn = hardCodedAllLocale?.cn;

  switch (currentLocale) {
    case hardCodedAllLocale?.en:
      checkInDate = moment(hotelData?.policies?.checkInTime, 'HH:mm a').format('hh:mm A');
      checkOutDate = moment(hotelData?.policies?.checkOutTime, 'HH:mm a').format('hh:mm A');
      break;
    case hardCodedAllLocale?.cn:
      moment.locale(localeCn.toLowerCase());
      checkInDate = moment(hotelData?.policies?.checkInTime, 'HH:mm').format('Ahh:mm');
      checkOutDate = moment(hotelData?.policies?.checkOutTime, 'HH:mm').format('Ahh:mm');
      break;
    case hardCodedAllLocale?.ar:
      checkInDate = getTimeInArabicTimeOfDayFormat(hotelData?.policies?.checkInTime);
      checkOutDate = getTimeInArabicTimeOfDayFormat(hotelData?.policies?.checkOutTime);
      break;
    default:
      checkInDate = hotelData?.policies?.checkInTime ? hotelData?.policies?.checkInTime : '';
      checkOutDate = hotelData?.policies?.checkOutTime ? hotelData?.policies?.checkOutTime : '';
      break;
  }

  const smokeFreePolicyText = jsonContent?.hotelInfoListItems[0]?.hotelInfoText;
  const smokeFreeLink = jsonContent?.hotelInfoListItems[0]?.hotelInfoLink;
  const clickTrackingLoc = 'HQV AEM Page';

  const isViewportL = useCheckBreakpoint('viewportL');
  const isViewportM = useCheckBreakpoint('viewportM');

  return (
    <Styled>
      <div
        className={clsx(
          pageVal === 'chat' ? (isViewportL ? 'pt-0' : 'pt-0 pb-4') : '',
          't-font-m',
          'information-box',
          `${hqvState.modalOpenState ? 'hqv-modal' : ''}`
        )}
      >
        <div
          className={clsx(
            pageVal === 'chat' ? (isViewportL ? 'mb-2' : 'mb-3') : '',
            'col',
            'm-icon-text',
            'left-col-item'
          )}
        >
          <span
            className={clsx(
              pageVal === 'chat'
                ? 'icon-s'
                : isViewportL
                ? 'icon-m icon-decorative left-col-icon'
                : 'icon-s icon-decorative left-col-icon',
              'icon-check-in',
              'check-in'
            )}
          ></span>
          <span
            className={clsx(
              pageVal === 'chat' ? 't-font-s' : isViewportL ? 't-font-m' : isViewportM ? 't-font-s' : 't-font-xs',
              'left-col-text'
            )}
          >
            {pageVal === 'chat' ? toSentenceCaseLabel(jsonContent?.checkInLabel) : jsonContent?.checkInLabel}:{' '}
            {hotelData?.policies ? checkInDate : ''}
          </span>
        </div>

        <div
          className={clsx(
            pageVal === 'chat' ? (isViewportL ? 'mb-2' : 'mb-3') : '',
            'col',
            'm-icon-text',
            'left-col-item'
          )}
        >
          <span
            className={clsx(
              pageVal === 'chat'
                ? 'icon-s'
                : isViewportL
                ? 'icon-m icon-decorative left-col-icon'
                : 'icon-s icon-decorative left-col-icon',
              'icon-check-out',
              'check-out'
            )}
          ></span>
          <span
            className={clsx(
              pageVal === 'chat' ? 't-font-s' : isViewportL ? 't-font-m' : isViewportM ? 't-font-s' : 't-font-xs',
              'left-col-text'
            )}
          >
            {pageVal === 'chat' ? toSentenceCaseLabel(jsonContent?.checkOutLabel) : jsonContent?.checkOutLabel}:{' '}
            {hotelData?.policies ? checkOutDate : ''}
          </span>
        </div>

        {hotelData && (
          <div
            className={clsx(
              pageVal === 'chat' ? (isViewportL ? 'mb-2' : 'mb-3') : '',
              'col',
              'm-icon-text',
              'left-col-item'
            )}
          >
            {!hotelData?.policies?.smokefree && jsonContent?.noSmokeFreePolicyText && (
              <>
                <span
                  className={clsx(
                    pageVal === 'chat'
                      ? 'icon-s'
                      : isViewportL
                      ? 'icon-m icon-decorative left-col-icon'
                      : 'icon-s icon-decorative left-col-icon',
                    'icon-smoke-free-property',
                    'smoke-free',
                    'left-col-icon'
                  )}
                ></span>
                <span
                  className={clsx(
                    pageVal === 'chat' ? 't-font-s' : isViewportL ? 't-font-m' : isViewportM ? 't-font-s' : 't-font-xs',
                    'left-col-text'
                  )}
                >
                  {jsonContent?.noSmokeFreePolicyText}
                </span>
              </>
            )}
            {hotelData?.policies?.smokefree && smokeFreePolicyText && (
              <>
                <span
                  className={clsx(
                    pageVal === 'chat'
                      ? 'icon-s'
                      : isViewportL
                      ? 'icon-m icon-decorative left-col-icon'
                      : 'icon-s icon-decorative left-col-icon',
                    'icon-opt-out',
                    'smoke-free'
                  )}
                ></span>
                <span
                  className={clsx(
                    pageVal === 'chat'
                      ? 't-font-s'
                      : isViewportL
                      ? 't-subtitle-m'
                      : isViewportM
                      ? 't-label-alt-s'
                      : 't-label-alt-xs',
                    'left-col-text',
                    'bold-item'
                  )}
                >
                  <a
                    href={smokeFreeLink}
                    target="_blank"
                    rel="noreferrer"
                    className="custom_click_track"
                    {...{ custom_click_track_value: `${clickTrackingLoc}| Smoke free policy |internal` }}
                  >
                    {smokeFreePolicyText}
                    <span role="note" aria-label="Opens in a new browser window."></span>
                  </a>
                </span>
              </>
            )}
          </div>
        )}
        {!jsonContent?.isPropertyDetailsSectionDisabled && (
          <div
            className={clsx(
              pageVal === 'chat' ? (isViewportL ? 'mb-2' : 'mb-3') : '',
              'col',
              'm-icon-text',
              'left-col-item'
            )}
          >
            <span
              className={clsx(
                pageVal === 'chat'
                  ? 'icon-s'
                  : isViewportL
                  ? 'icon-m icon-decorative left-col-icon'
                  : 'icon-s icon-decorative left-col-icon',
                'icon-accessibility',
                'check-out'
              )}
            ></span>
            <span
              className={clsx(
                pageVal === 'chat'
                  ? 't-font-s'
                  : isViewportL
                  ? 't-subtitle-m'
                  : isViewportM
                  ? 't-label-alt-s'
                  : 't-label-alt-xs',
                'left-col-text',
                'bold-item'
              )}
            >
              <a
                href="#hqv-property-details-section"
                className="custom_click_track"
                {...{ custom_click_track_value: `${clickTrackingLoc}| Accessiblity link clicked |internal` }}
              >
                {accessibility}
              </a>
            </span>
          </div>
        )}
        {jsonContent?.isPropertyDetailsSectionDisabled && (
          <div
            className={clsx(
              pageVal === 'chat' ? (isViewportL ? 'mb-2' : 'mb-3') : '',
              'col',
              'm-icon-text',
              'left-col-item'
            )}
          >
            <span
              className={clsx(
                pageVal === 'chat'
                  ? 'icon-s'
                  : isViewportL
                  ? 'icon-m icon-decorative left-col-icon'
                  : 'icon-s icon-decorative left-col-icon',
                'icon-accessibility',
                'check-out'
              )}
            ></span>
            <span
              className={clsx(
                pageVal === 'chat'
                  ? 't-font-s'
                  : isViewportL
                  ? 't-subtitle-m'
                  : isViewportM
                  ? 't-label-alt-s'
                  : 't-label-alt-xs',
                'left-col-text',
                'bold-item'
              )}
            >
              <a
                href={
                  addSubDirectoryPrefix(NAVIGATION_URL_CONSTANTS.SM_VIEW_HOTEL_WEBSITE_LINK) +
                  hotelData?.id?.toLowerCase() +
                  '-' +
                  hotelData?.basicInformation?.name
                    .replace('-', ' ')
                    .replace(/([~!@#$%^&*()_+=`{}[\]|\\:;'<>,./? ])+/g, '-')
                    .replace(/^(-)+|(-)+$/g, '')
                    ?.toLowerCase()
                }
                target="_blank"
                rel="noreferrer"
                className="custom_click_track"
                {...{ custom_click_track_value: `${clickTrackingLoc}| Accessiblity link clicked |internal` }}
              >
                {accessibility}
              </a>
            </span>
          </div>
        )}
      </div>
    </Styled>
  );
};
